import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, UserPlus, ShieldCheck, XCircle, Smartphone } from "lucide-react";

export default function Register() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { completeLogin } = useAuth();

  const [checking, setChecking] = useState(true);
  const [valid, setValid] = useState(false);
  const [step, setStep] = useState("form"); // form | mfa

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  const [mfaToken, setMfaToken] = useState("");
  const [qr, setQr] = useState("");
  const [manualKey, setManualKey] = useState("");
  const [code, setCode] = useState("");

  useEffect(() => {
    api.get(`/invites/validate/${token}`).then(({ data }) => setValid(data.valid))
      .catch(() => setValid(false)).finally(() => setChecking(false));
  }, [token]);

  const submitForm = async (e) => {
    e.preventDefault();
    if (!/^(?=.*[A-Z])(?=.*[^A-Za-z0-9]).{8,}$/.test(password)) {
      toast.error("Password must be at least 8 characters with one capital letter and one special character");
      return;
    }
    if (password !== confirm) { toast.error("Passwords do not match"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register-invite", { token, name, email, password });
      setMfaToken(data.mfa_token);
      setQr(data.qr || "");
      setManualKey(data.manual_key || "");
      setStep("mfa");
      toast.success("Account created — now register your authenticator");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Registration failed");
    } finally { setLoading(false); }
  };

  const submitMfa = async (e) => {
    e.preventDefault();
    if (code.trim().length < 6) { toast.error("Enter the 6-digit code"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/2fa/verify", { mfa_token: mfaToken, code: code.trim() });
      completeLogin(data.token, data.user);
      toast.success("Signed in");
      navigate("/");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Verification failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center p-6 scanlines">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img src="/logo.png" alt="Soverenx Systems" className="h-14 mx-auto object-contain" />
          <div className="text-[10px] tracking-[0.35em] uppercase text-[#484F58] mt-3 font-mono-jb">Invitation · Create your account</div>
        </div>

        <div className="border border-white/10 bg-[#0B0C10] p-8">
          {checking ? (
            <div className="font-mono-jb text-[#8B949E] text-center py-6"><Loader2 className="animate-spin inline mr-2" size={16} /> Validating invite...</div>
          ) : !valid ? (
            <div className="text-center py-6" data-testid="invite-invalid">
              <XCircle className="mx-auto text-[#FF3B30] mb-3" size={36} />
              <div className="font-mono-jb text-white mb-1">Invite unavailable</div>
              <p className="text-[#8B949E] text-sm">This invite link is invalid or has already been used.</p>
              <Link to="/login" className="inline-block mt-4 text-[#00F0FF] font-mono-jb text-xs uppercase tracking-wider hover:underline">Go to sign in</Link>
            </div>
          ) : step === "mfa" ? (
            <form onSubmit={submitMfa} className="space-y-5" data-testid="reg-mfa-form">
              <div className="flex items-center gap-2 mb-1">
                <ShieldCheck className="text-[#00FF41]" size={18} />
                <h1 className="font-mono-jb text-xl font-bold tracking-tight text-white">Register authenticator</h1>
              </div>
              <div className="border border-[#00F0FF]/20 p-4 space-y-3">
                <div className="flex items-center gap-2 text-[#00F0FF] text-xs font-mono-jb uppercase tracking-wider"><Smartphone size={14} /> One-time setup</div>
                <p className="text-[11px] text-[#8B949E] leading-relaxed">Scan this QR with Microsoft Authenticator, Google Authenticator or Authy, then enter the 6-digit code.</p>
                {qr && <img data-testid="reg-mfa-qr" src={qr} alt="2FA QR code" className="w-44 h-44 mx-auto bg-white p-2 rounded-sm" />}
                {manualKey && <div className="text-[10px] font-mono-jb text-[#484F58] break-all text-center">Manual key: <span className="text-[#8B949E]">{manualKey}</span></div>}
              </div>
              <Input data-testid="reg-mfa-code" inputMode="numeric" maxLength={6} value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} placeholder="000000"
                className="bg-[#0B0C10] border-white/15 font-mono-jb text-center text-2xl tracking-[0.5em] focus-visible:ring-[#00FF41]" />
              <Button data-testid="reg-mfa-submit" type="submit" disabled={loading}
                className="w-full rounded-sm bg-[#00FF41] text-black font-mono-jb uppercase text-xs tracking-widest font-bold hover:bg-[#00FF41] h-11">
                {loading ? <Loader2 className="animate-spin" size={16} /> : "Verify & Sign in"}
              </Button>
            </form>
          ) : (
            <form onSubmit={submitForm} className="space-y-4" data-testid="register-form">
              <div className="flex items-center gap-2 mb-1">
                <UserPlus className="text-[#00F0FF]" size={18} />
                <h1 className="font-mono-jb text-xl font-bold tracking-tight text-white">Create account</h1>
              </div>
              <div>
                <label className="text-[10px] tracking-[0.2em] uppercase text-[#8B949E] font-mono-jb">Full Name</label>
                <Input data-testid="reg-name" value={name} onChange={(e) => setName(e.target.value)} className="mt-2 bg-[#0B0C10] border-white/15 font-mono-jb focus-visible:ring-[#00F0FF]" />
              </div>
              <div>
                <label className="text-[10px] tracking-[0.2em] uppercase text-[#8B949E] font-mono-jb">Email</label>
                <Input data-testid="reg-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-2 bg-[#0B0C10] border-white/15 font-mono-jb focus-visible:ring-[#00F0FF]" />
              </div>
              <div>
                <label className="text-[10px] tracking-[0.2em] uppercase text-[#8B949E] font-mono-jb">Password</label>
                <Input data-testid="reg-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="mt-2 bg-[#0B0C10] border-white/15 font-mono-jb focus-visible:ring-[#00F0FF]" />
                <p className="text-[10px] text-[#484F58] mt-1.5 font-mono-jb">Min 8 chars · 1 capital letter · 1 special character.</p>
              </div>
              <div>
                <label className="text-[10px] tracking-[0.2em] uppercase text-[#8B949E] font-mono-jb">Confirm Password</label>
                <Input data-testid="reg-confirm" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className="mt-2 bg-[#0B0C10] border-white/15 font-mono-jb focus-visible:ring-[#00F0FF]" />
              </div>
              <div className="flex items-center gap-2 text-[10px] text-[#8B949E] font-mono-jb">
                <ShieldCheck size={12} className="text-[#00FF41]" /> You'll register your authenticator next.
              </div>
              <Button data-testid="reg-submit" type="submit" disabled={loading}
                className="w-full rounded-sm bg-[#00F0FF] text-black font-mono-jb uppercase text-xs tracking-widest font-bold hover:bg-[#00F0FF] h-11">
                {loading ? <Loader2 className="animate-spin" size={16} /> : "Continue"}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
